/*
 * <sd-select> — the custom dropdown from snippets/sd-select.liquid.
 *
 * Open/close is the native <details>, so this only handles choosing: write the
 * hidden input (that's what the form posts), repaint the trigger label, move the
 * aria-selected flag, close.
 *
 * Also closes on outside click and Escape — <details> does neither on its own,
 * and a dropdown left hanging open over the next field is the usual complaint.
 */
class SdSelect extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.input = this.querySelector('[data-sd-select-input]');
    this.labelEl = this.querySelector('[data-sd-select-label]');
    this.summary = this.querySelector('summary');
    this.placeholder = this.labelEl?.textContent.trim();

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // `toggle` fires on the <details>; mirror it onto the combobox for screen readers.
    this.details?.addEventListener('toggle', () => {
      this.summary?.setAttribute('aria-expanded', String(this.details.open));
    });

    this.onDocumentClick = (event) => {
      if (this.details?.open && !this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  onClick(event) {
    const option = event.target.closest('.sd-dropdown__option');
    if (!option || !this.contains(option)) return;

    event.preventDefault();
    this.select(option);
  }

  onKeydown(event) {
    if (event.key !== 'Escape' || !this.details?.open) return;
    this.close();
    this.summary?.focus();
  }

  select(option) {
    const value = option.dataset.value || '';

    if (this.input) this.input.value = value;
    if (this.labelEl) {
      this.labelEl.textContent = value || this.placeholder;
      // Only the untouched state is greyed; a real choice reads at full ink.
      this.labelEl.classList.toggle('sd-dropdown__value--placeholder', !value);
    }

    this.querySelectorAll('.sd-dropdown__option').forEach((el) => {
      el.setAttribute('aria-selected', String(el === option));
    });

    this.close();
    // Let a host section react (validation, dependent fields) without reaching in here.
    this.dispatchEvent(new CustomEvent('sd-select:change', { bubbles: true, detail: { value } }));
  }

  close() {
    if (this.details) this.details.open = false;
  }
}

if (!customElements.get('sd-select')) customElements.define('sd-select', SdSelect);
